import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, Calendar as CalendarIcon, Newspaper, 
  Plus, Pin, Trash2, Send, Clock, X, Paperclip, FileText, Download,
  Users, Heart, ThumbsUp, Smile, Image, BarChart3, MapPin, Search, Edit
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reaction types with emojis
const REACTIONS = {
  like: { emoji: '👍', label: 'Mi piace' },
  love: { emoji: '❤️', label: 'Adoro' },
  laugh: { emoji: '😂', label: 'Ahah' },
  wow: { emoji: '😮', label: 'Wow' },
  sad: { emoji: '😢', label: 'Triste' },
  angry: { emoji: '😡', label: 'Arrabbiato' }
};

// Presence status options
const PRESENCE_STATUS = {
  in_ufficio: { label: 'In ufficio', color: 'bg-green-500', emoji: '🏢' },
  smart_working: { label: 'Smart Working', color: 'bg-blue-500', emoji: '🏠' },
  fuori_sede: { label: 'Fuori sede', color: 'bg-orange-500', emoji: '🚗' },
  ferie: { label: 'Ferie', color: 'bg-purple-500', emoji: '🏖️' },
  malattia: { label: 'Malattia', color: 'bg-red-500', emoji: '🤒' }
};

function Board() {
  const [activeTab, setActiveTab] = useState('home');
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [newsArticles, setNewsArticles] = useState([]);
  const [weather, setWeather] = useState(null);
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  
  // Social states
  const [socialFeed, setSocialFeed] = useState([]);
  const [showSocialPostModal, setShowSocialPostModal] = useState(false);
  const [selectedSocialPost, setSelectedSocialPost] = useState(null);
  const [socialComments, setSocialComments] = useState([]);
  const [presenceList, setPresenceList] = useState([]);
  const [myPresence, setMyPresence] = useState(null);
  const [showPresenceModal, setShowPresenceModal] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUser(user);
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (activeTab === 'home') {
        // Fetch all data for homepage
        const [postsRes, eventsRes, newsRes, weatherRes, birthdaysRes] = await Promise.all([
          fetch(`${API_URL}/api/board/posts?limit=5`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/board/events`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/board/news?limit=10`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/weather`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/birthdays?days_ahead=14`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        if (postsRes.ok) setPosts(await postsRes.json());
        if (eventsRes.ok) {
          const eventsData = await eventsRes.json();
          // Filter next 5 events
          setEvents(eventsData.slice(0, 5));
        }
        if (newsRes.ok) setNewsArticles(await newsRes.json());
        if (weatherRes.ok) setWeather(await weatherRes.json());
        if (birthdaysRes.ok) setBirthdays(await birthdaysRes.json());
        
      } else if (activeTab === 'annunci') {
        const res = await fetch(`${API_URL}/api/board/posts`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          setPosts(data);
        }
      } else if (activeTab === 'calendario') {
        const res = await fetch(`${API_URL}/api/board/events`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          setEvents(data);
        }
      } else if (activeTab === 'news') {
        const res = await fetch(`${API_URL}/api/board/news?limit=20`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          setNewsArticles(data);
        }
      } else if (activeTab === 'community') {
        // Fetch social feed and presence
        const [feedRes, presenceRes] = await Promise.all([
          fetch(`${API_URL}/api/social/feed`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/presence`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        
        if (feedRes.ok) setSocialFeed(await feedRes.json());
        if (presenceRes.ok) setPresenceList(await presenceRes.json());
      }

      setLoading(false);
    } catch (err) {
      console.error('Error:', err);
      setLoading(false);
    }
  };

  const openPostDetail = async (post) => {
    setSelectedPost(post);
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/board/posts/${post._id}/comments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setComments(data);
      }
    } catch (err) {
      console.error('Error loading comments:', err);
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;

    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/board/posts/${selectedPost._id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          post_id: selectedPost._id,
          contenuto: newComment
        })
      });

      if (!res.ok) throw new Error('Errore invio commento');

      const comment = await res.json();
      setComments([...comments, comment]);
      setNewComment('');
    } catch (err) {
      alert(err.message);
    }
  };

  const canCreatePost = currentUser?.role === 'admin' || currentUser?.role === 'hse_manager';
  const isAdmin = currentUser?.role === 'admin';
  const canManageEvents = currentUser?.role === 'admin' || currentUser?.role === 'hse_manager';

  // Delete announcement (admin only)
  const deleteAnnouncement = async (postId, e) => {
    e.stopPropagation(); // Prevent opening the detail modal
    
    if (!window.confirm('Sei sicuro di voler eliminare questa comunicazione?')) {
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/board/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        // Remove from local state
        setPosts(posts.filter(p => p._id !== postId));
        alert('Comunicazione eliminata con successo');
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante l\'eliminazione');
      }
    } catch (err) {
      console.error('Error deleting announcement:', err);
      alert('Errore di connessione');
    }
  };

  // Delete event (admin/HSE only)
  const deleteEvent = async (eventId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Sei sicuro di voler eliminare questo evento?')) {
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/board/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setEvents(events.filter(e => e._id !== eventId));
        alert('Evento eliminato con successo');
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante l\'eliminazione');
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Errore di connessione');
    }
  };

  // Edit event state
  const [editingEvent, setEditingEvent] = useState(null);

  // Open edit event modal
  const openEditEvent = (event, e) => {
    e.stopPropagation();
    setEditingEvent(event);
    setShowEventModal(true);
  };

  // Social functions
  const reactToPost = async (postId, tipo) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/social/posts/${postId}/react?tipo=${tipo}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        // Refresh feed
        const feedRes = await fetch(`${API_URL}/api/social/feed`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (feedRes.ok) setSocialFeed(await feedRes.json());
      }
    } catch (err) {
      console.error('Error reacting:', err);
    }
  };

  const openSocialPostDetail = async (post) => {
    setSelectedSocialPost(post);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/social/posts/${post._id}/comments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setSocialComments(await res.json());
    } catch (err) {
      console.error('Error loading comments:', err);
    }
  };

  const addSocialComment = async (contenuto) => {
    if (!contenuto.trim() || !selectedSocialPost) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/social/posts/${selectedSocialPost._id}/comments?contenuto=${encodeURIComponent(contenuto)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const comment = await res.json();
        setSocialComments([...socialComments, comment]);
      }
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  };

  const updateMyPresence = async (status, location = null, note = null) => {
    const token = localStorage.getItem('token');
    try {
      const params = new URLSearchParams({ status });
      if (location) params.append('location', location);
      if (note) params.append('note', note);
      
      await fetch(`${API_URL}/api/presence?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setMyPresence(status);
      setShowPresenceModal(false);
      
      // Refresh presence list
      const res = await fetch(`${API_URL}/api/presence`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setPresenceList(await res.json());
    } catch (err) {
      console.error('Error updating presence:', err);
    }
  };

  const deleteSocialPost = async (postId) => {
    if (!window.confirm('Eliminare questo post?')) return;
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/social/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSocialFeed(socialFeed.filter(p => p._id !== postId));
      setSelectedSocialPost(null);
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const votePoll = async (postId, optionIndex) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/social/polls/${postId}/vote?opzione_index=${optionIndex}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Refresh feed
      const feedRes = await fetch(`${API_URL}/api/social/feed`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (feedRes.ok) setSocialFeed(await feedRes.json());
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  return (
    <div data-testid="board-page">
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Bacheca Aziendale</h1>
            <p className="page-description">
              Annunci, comunicazioni e calendario eventi
            </p>
          </div>
          {canCreatePost && (
            <div className="flex gap-2">
              {activeTab === 'annunci' && (
                <button
                  className="btn btn-primary"
                  onClick={() => setShowPostModal(true)}
                  data-testid="create-post-button"
                >
                  <Plus size={20} />
                  Nuovo Annuncio
                </button>
              )}
              {activeTab === 'calendario' && (
                <button
                  className="btn btn-primary"
                  onClick={() => setShowEventModal(true)}
                  data-testid="create-event-button"
                >
                  <Plus size={20} />
                  Nuovo Evento
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-border">
        <div className="flex gap-1">
          <button
            className={`px-4 py-3 font-medium transition-colors ${
              activeTab === 'home'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('home')}
            data-testid="tab-home"
          >
            🏠 Home
          </button>
          <button
            className={`px-4 py-3 font-medium transition-colors ${
              activeTab === 'annunci'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('annunci')}
            data-testid="tab-annunci"
          >
            <MessageSquare size={18} className="inline mr-2" />
            Annunci
          </button>
          <button
            className={`px-4 py-3 font-medium transition-colors ${
              activeTab === 'news'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('news')}
            data-testid="tab-news"
          >
            <Newspaper size={18} className="inline mr-2" />
            News di Settore
          </button>
          <button
            className={`px-4 py-3 font-medium transition-colors ${
              activeTab === 'calendario'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('calendario')}
            data-testid="tab-calendario"
          >
            <CalendarIcon size={18} className="inline mr-2" />
            Calendario
          </button>
        </div>
      </div>

      {/* Home Tab - Interactive Dashboard */}
      {activeTab === 'home' && (
        <div>
          {loading ? (
            <div className="text-center py-8">
              <p>Caricamento...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left Column - Calendar Highlight */}
              <div className="lg:col-span-4 space-y-6">
                {/* Big Calendar Widget */}
                <div className="card bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20" data-testid="calendar-widget">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold flex items-center gap-2">
                      <CalendarIcon size={24} className="text-primary" />
                      Calendario Eventi
                    </h3>
                    {canCreatePost && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowEventModal(true)}
                        data-testid="quick-add-event"
                      >
                        <Plus size={16} />
                        Nuovo
                      </button>
                    )}
                  </div>
                  
                  {/* Today's Date Display */}
                  <div className="text-center py-4 mb-4 bg-primary/10 rounded-xl">
                    <p className="text-sm text-muted-foreground uppercase tracking-wide">
                      {format(new Date(), 'EEEE', { locale: it })}
                    </p>
                    <p className="text-5xl font-bold text-primary my-1">
                      {format(new Date(), 'd')}
                    </p>
                    <p className="text-lg font-medium">
                      {format(new Date(), 'MMMM yyyy', { locale: it })}
                    </p>
                  </div>
                  
                  {/* Upcoming Events List */}
                  {events.length === 0 ? (
                    <div className="text-center py-6">
                      <CalendarIcon size={40} className="mx-auto text-muted-foreground/50 mb-2" />
                      <p className="text-sm text-muted-foreground">Nessun evento in programma</p>
                      {canCreatePost && (
                        <button
                          className="btn btn-primary btn-sm mt-3"
                          onClick={() => setShowEventModal(true)}
                        >
                          <Plus size={14} /> Aggiungi Evento
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {events.slice(0, 6).map((event, index) => {
                        const eventDate = new Date(event.data_inizio);
                        const isToday = format(eventDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                        
                        return (
                          <div
                            key={event._id}
                            className={`p-3 rounded-lg border transition-all hover:shadow-md ${
                              isToday 
                                ? 'bg-primary/15 border-primary/30 shadow-sm' 
                                : 'bg-card border-border hover:border-primary/30'
                            }`}
                            data-testid="home-event"
                          >
                            <div className="flex gap-3">
                              <div className={`flex flex-col items-center justify-center px-3 py-1 rounded-lg ${
                                isToday ? 'bg-primary text-white' : 'bg-muted'
                              }`}>
                                <span className="text-xs font-medium uppercase">
                                  {format(eventDate, 'MMM', { locale: it })}
                                </span>
                                <span className="text-xl font-bold">
                                  {format(eventDate, 'd')}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm truncate">{event.titolo}</p>
                                <p className="text-xs text-muted-foreground">
                                  {format(eventDate, 'HH:mm', { locale: it })}
                                  {event.location && ` • 📍 ${event.location}`}
                                </p>
                                <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                                  event.tipo === 'formazione' ? 'bg-green-100 text-green-700' :
                                  event.tipo === 'scadenza' ? 'bg-red-100 text-red-700' :
                                  event.tipo === 'meeting' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {event.tipo}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <button
                        className="w-full btn btn-secondary btn-sm"
                        onClick={() => setActiveTab('calendario')}
                      >
                        Vedi tutti gli eventi →
                      </button>
                    </div>
                  )}
                </div>

                {/* Birthdays Widget - Enhanced & Larger */}
                <div className="card bg-gradient-to-br from-pink-50 to-orange-50 dark:from-pink-900/10 dark:to-orange-900/10 border-pink-200/50" data-testid="birthdays-widget">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold flex items-center gap-2">
                      🎂 Compleanni in Arrivo
                    </h3>
                    <span className="text-sm text-muted-foreground">
                      Prossimi 14 giorni
                    </span>
                  </div>
                  
                  {birthdays.length === 0 ? (
                    <div className="text-center py-6">
                      <span className="text-4xl mb-2 block">🎈</span>
                      <p className="text-muted-foreground">
                        Nessun compleanno nei prossimi 14 giorni
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {birthdays.slice(0, 6).map((bday) => {
                        const avatarColor = bday.sesso === 'M' 
                          ? 'bg-gradient-to-br from-blue-400 to-blue-600' 
                          : bday.sesso === 'F' 
                          ? 'bg-gradient-to-br from-pink-400 to-pink-600' 
                          : 'bg-gradient-to-br from-purple-400 to-purple-600';
                        
                        return (
                          <div
                            key={bday._id}
                            className={`bg-white dark:bg-card rounded-xl p-4 shadow-sm transition-all hover:shadow-md ${
                              bday.is_today
                                ? 'ring-2 ring-primary ring-offset-2 animate-pulse'
                                : ''
                            }`}
                            data-testid="birthday-item"
                          >
                            <div className="flex items-center gap-4">
                              {bday.foto_url ? (
                                <div className="relative flex-shrink-0">
                                  <img
                                    src={`${API_URL}${bday.foto_url}`}
                                    alt={`${bday.nome} ${bday.cognome}`}
                                    className="w-16 h-16 rounded-full object-cover ring-4 ring-white shadow-lg"
                                  />
                                  {bday.is_today && (
                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 text-white rounded-full flex items-center justify-center text-lg shadow-lg animate-bounce">
                                      🎉
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div 
                                  className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-lg ring-4 ring-white shadow-lg flex-shrink-0 ${avatarColor}`}
                                >
                                  {bday.nome?.charAt(0)}{bday.cognome?.charAt(0)}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-base truncate">
                                  {bday.nome} {bday.cognome}
                                </p>
                                {bday.is_today ? (
                                  <div className="mt-1">
                                    <span className="inline-block bg-primary text-white text-sm font-semibold px-3 py-1 rounded-full">
                                      🎉 Oggi compie {bday.age} anni!
                                    </span>
                                  </div>
                                ) : (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    <span className="font-semibold text-primary">
                                      {bday.days_until === 1 ? 'Domani' : `Tra ${bday.days_until} giorni`}
                                    </span>
                                    <span className="mx-1">•</span>
                                    Compirà {bday.age} anni
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Center Column - Announcements */}
              <div className="lg:col-span-5 space-y-6">
                {/* Announcements Widget - Enhanced */}
                <div className="card" data-testid="announcements-widget">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold flex items-center gap-2">
                      <MessageSquare size={24} className="text-primary" />
                      Comunicazioni
                    </h3>
                    <div className="flex gap-2">
                      {canCreatePost && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => setShowPostModal(true)}
                          data-testid="quick-add-post"
                        >
                          <Plus size={14} />
                          Pubblica
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {posts.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageSquare size={48} className="mx-auto text-muted-foreground/30 mb-3" />
                      <p className="text-muted-foreground mb-3">Nessuna comunicazione recente</p>
                      {canCreatePost && (
                        <button
                          className="btn btn-primary"
                          onClick={() => setShowPostModal(true)}
                        >
                          <Plus size={16} />
                          Pubblica il primo annuncio
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {posts.map((post, index) => {
                        const isFirst = index === 0;
                        const categoryColors = {
                          'annuncio': 'bg-blue-500',
                          'comunicazione': 'bg-green-500',
                          'alert': 'bg-red-500'
                        };
                        
                        return (
                          <div
                            key={post._id}
                            className={`group relative rounded-xl border transition-all cursor-pointer hover:shadow-lg ${
                              isFirst 
                                ? 'bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20 p-5' 
                                : 'bg-card border-border p-4 hover:border-primary/30'
                            }`}
                            onClick={() => openPostDetail(post)}
                            data-testid="home-post"
                          >
                            {/* Category indicator */}
                            <div className={`absolute top-0 left-4 w-1 h-full rounded-full ${categoryColors[post.categoria] || 'bg-gray-400'}`} style={{left: '-2px', height: 'calc(100% - 16px)', top: '8px'}} />
                            
                            <div className="flex items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  {post.pinned && (
                                    <Pin size={14} className="text-primary flex-shrink-0" />
                                  )}
                                  <h4 className={`font-bold truncate ${isFirst ? 'text-lg' : 'text-base'}`}>
                                    {post.titolo}
                                  </h4>
                                </div>
                                <p className={`text-muted-foreground mb-3 ${isFirst ? 'line-clamp-3' : 'line-clamp-2 text-sm'}`}>
                                  {post.contenuto}
                                </p>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                      <Clock size={12} />
                                      {format(new Date(post.created_at), 'dd MMM', { locale: it })}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      👤 {post.author_nome}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <MessageSquare size={12} />
                                      {post.comments_count || 0}
                                    </span>
                                    {post.allegati && post.allegati.length > 0 && (
                                      <span className="flex items-center gap-1 text-primary">
                                        <Paperclip size={12} />
                                        {post.allegati.length}
                                      </span>
                                    )}
                                  </div>
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    post.categoria === 'alert' 
                                      ? 'bg-red-100 text-red-700' 
                                      : post.categoria === 'comunicazione'
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {post.categoria}
                                  </span>
                                </div>
                              </div>
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity text-primary">
                                →
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <button
                        className="w-full btn btn-secondary"
                        onClick={() => setActiveTab('annunci')}
                      >
                        Vedi tutte le comunicazioni →
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Weather & News (Compact) */}
              <div className="lg:col-span-3 space-y-4">
                {/* Compact Weather Widget */}
                <div className="card p-4" data-testid="weather-widget">
                  {weather && weather.status === 'success' ? (
                    <div className="flex items-center gap-3">
                      <img
                        src={weather.icon_url}
                        alt={weather.description}
                        className="w-14 h-14"
                      />
                      <div className="flex-1">
                        <p className="text-2xl font-bold">{weather.temperature}°C</p>
                        <p className="text-xs text-muted-foreground capitalize">{weather.description}</p>
                        <p className="text-xs text-muted-foreground">📍 {weather.location}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-2xl">
                        🌤️
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm">Meteo</p>
                        <p className="text-xs text-muted-foreground">
                          {weather?.message?.includes('non configurata') 
                            ? 'API key non configurata'
                            : 'Caricamento...'}
                        </p>
                        {currentUser?.role === 'admin' && (
                          <a href="/settings" className="text-xs text-primary hover:underline">
                            Configura →
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Compact News Widget */}
                <div className="card p-4" data-testid="news-widget">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                      <Newspaper size={16} className="text-primary" />
                      News Settore
                    </h3>
                    <button
                      className="text-xs text-primary hover:underline"
                      onClick={() => setActiveTab('news')}
                    >
                      Tutte →
                    </button>
                  </div>
                  
                  {newsArticles.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      Configura i feed RSS
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {newsArticles.slice(0, 3).map((article, index) => (
                        <a
                          key={index}
                          href={article.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block group"
                          data-testid="home-news"
                        >
                          <div className="flex gap-2">
                            <div className="flex-shrink-0 w-12 h-12 bg-muted rounded overflow-hidden">
                              {article.image ? (
                                <img
                                  src={article.image}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-lg">📰</div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium line-clamp-2 group-hover:text-primary transition-colors">
                                {article.title}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {article.source_name}
                              </p>
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Annunci Tab */}
      {activeTab === 'annunci' && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8">
              <p>Caricamento...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="empty-state">
              <MessageSquare className="empty-state-icon" />
              <p>Nessun annuncio pubblicato</p>
              {canCreatePost && (
                <button
                  className="btn btn-primary mt-4"
                  onClick={() => setShowPostModal(true)}
                >
                  <Plus size={20} />
                  Pubblica Primo Annuncio
                </button>
              )}
            </div>
          ) : (
            posts.map((post) => (
              <div
                key={post._id}
                className="card hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openPostDetail(post)}
                data-testid="board-post"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {post.pinned && (
                        <Pin size={16} className="text-primary" />
                      )}
                      <h3 className="font-semibold text-lg">{post.titolo}</h3>
                      <span className="badge badge-secondary text-xs">
                        {post.categoria}
                      </span>
                    </div>
                    <p className="text-muted-foreground mb-2">{post.contenuto}</p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>
                        <Clock size={14} className="inline mr-1" />
                        {format(new Date(post.created_at), 'dd MMM yyyy HH:mm', { locale: it })}
                      </span>
                      <span>da {post.author_nome}</span>
                      <span>
                        <MessageSquare size={14} className="inline mr-1" />
                        {post.comments_count || 0} commenti
                      </span>
                    </div>
                  </div>
                  {/* Delete button - Admin only */}
                  {isAdmin && (
                    <button
                      className="btn btn-secondary text-destructive hover:bg-destructive hover:text-white p-2"
                      onClick={(e) => deleteAnnouncement(post._id, e)}
                      title="Elimina comunicazione"
                      data-testid={`delete-post-${post._id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* News Tab */}
      {activeTab === 'news' && (
        <div>
          {loading ? (
            <div className="text-center py-8">
              <p>Caricamento news...</p>
            </div>
          ) : newsArticles.length === 0 ? (
            <div className="card">
              <div className="text-center py-12">
                <Newspaper size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">News di Settore</h3>
                <p className="text-muted-foreground mb-4">
                  Configura i feed RSS nelle Impostazioni per vedere le news qui
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {newsArticles.map((article, index) => (
                <div
                  key={index}
                  className="card hover:shadow-md transition-shadow"
                  data-testid="news-article"
                >
                  <div className="flex gap-4">
                    {article.image && (
                      <div className="flex-shrink-0 w-32 h-32 bg-muted rounded-lg overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-lg">{article.title}</h3>
                        <span className="badge badge-secondary text-xs flex-shrink-0 ml-2">
                          {article.source_name}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {article.summary?.replace(/<[^>]*>/g, '')}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>
                            <Clock size={12} className="inline mr-1" />
                            {format(new Date(article.published), 'dd MMM yyyy HH:mm', { locale: it })}
                          </span>
                          {article.author && <span>• {article.author}</span>}
                        </div>
                        <a
                          href={article.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary btn-sm"
                          onClick={(e) => e.stopPropagation()}
                          data-testid="read-article"
                        >
                          Leggi articolo →
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Calendario Tab */}
      {activeTab === 'calendario' && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8">
              <p>Caricamento...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="empty-state">
              <CalendarIcon className="empty-state-icon" />
              <p>Nessun evento in calendario</p>
              {canCreatePost && (
                <button
                  className="btn btn-primary mt-4"
                  onClick={() => setShowEventModal(true)}
                >
                  <Plus size={20} />
                  Aggiungi Primo Evento
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map((event) => (
                <div key={event._id} className="card" data-testid="board-event">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold">{event.titolo}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="badge badge-secondary text-xs">
                        {event.tipo}
                      </span>
                      {/* Edit/Delete buttons - Admin/HSE only */}
                      {canManageEvents && (
                        <div className="flex gap-1">
                          <button
                            className="btn btn-secondary p-1.5 hover:bg-primary hover:text-white"
                            onClick={(e) => openEditEvent(event, e)}
                            title="Modifica evento"
                            data-testid={`edit-event-${event._id}`}
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            className="btn btn-secondary p-1.5 text-destructive hover:bg-destructive hover:text-white"
                            onClick={(e) => deleteEvent(event._id, e)}
                            title="Elimina evento"
                            data-testid={`delete-event-${event._id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {event.descrizione}
                  </p>
                  <div className="text-sm">
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <CalendarIcon size={14} />
                      {format(new Date(event.data_inizio), 'dd MMM yyyy HH:mm', { locale: it })}
                    </p>
                    {event.location && (
                      <p className="text-muted-foreground mt-1">📍 {event.location}</p>
                    )}
                  </div>
                  
                  {/* Event Attachments */}
                  {event.allegati && event.allegati.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                        <Paperclip size={12} className="text-primary" />
                        {event.allegati.length} allegat{event.allegati.length === 1 ? 'o' : 'i'}
                      </p>
                      <div className="space-y-1">
                        {event.allegati.map((att, idx) => (
                          <a
                            key={idx}
                            href={`${API_URL}/api/board/attachments/event/${event._id}/${encodeURIComponent(att.nome_file)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-xs text-primary hover:underline"
                          >
                            <FileText size={12} />
                            {att.nome_file}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Community Tab */}
      {activeTab === 'community' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main Feed */}
          <div className="lg:col-span-8 space-y-6">
            {/* Create Post Card */}
            <div className="card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold">
                  {currentUser?.nome?.charAt(0) || 'U'}
                </div>
                <button
                  className="flex-1 bg-muted/50 hover:bg-muted rounded-full px-4 py-3 text-left text-muted-foreground transition-colors"
                  onClick={() => setShowSocialPostModal(true)}
                  data-testid="create-social-post"
                >
                  Cosa stai pensando?
                </button>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                <button 
                  className="flex-1 btn btn-secondary btn-sm flex items-center justify-center gap-2"
                  onClick={() => setShowSocialPostModal(true)}
                >
                  <Image size={18} className="text-green-500" />
                  Foto/Video
                </button>
                <button 
                  className="flex-1 btn btn-secondary btn-sm flex items-center justify-center gap-2"
                  onClick={() => setShowSocialPostModal(true)}
                >
                  <BarChart3 size={18} className="text-orange-500" />
                  Sondaggio
                </button>
              </div>
            </div>

            {/* Feed Posts */}
            {loading ? (
              <div className="text-center py-8">
                <p>Caricamento...</p>
              </div>
            ) : socialFeed.length === 0 ? (
              <div className="card text-center py-12">
                <Users size={48} className="mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="font-semibold text-lg mb-2">Nessun post ancora</h3>
                <p className="text-muted-foreground mb-4">
                  Sii il primo a condividere qualcosa con i tuoi colleghi!
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowSocialPostModal(true)}
                >
                  <Plus size={18} />
                  Crea il primo post
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {socialFeed.map((post) => (
                  <SocialPostCard
                    key={post._id}
                    post={post}
                    currentUser={currentUser}
                    onReact={reactToPost}
                    onComment={() => openSocialPostDetail(post)}
                    onDelete={deleteSocialPost}
                    onVote={votePoll}
                    API_URL={API_URL}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Sidebar - Presence */}
          <div className="lg:col-span-4 space-y-4">
            {/* My Presence Status */}
            <div className="card">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <MapPin size={18} className="text-primary" />
                Il tuo stato
              </h3>
              <button
                className="w-full btn btn-secondary justify-between"
                onClick={() => setShowPresenceModal(true)}
                data-testid="update-presence"
              >
                <span className="flex items-center gap-2">
                  {myPresence ? (
                    <>
                      <span>{PRESENCE_STATUS[myPresence]?.emoji}</span>
                      {PRESENCE_STATUS[myPresence]?.label}
                    </>
                  ) : (
                    'Imposta il tuo stato'
                  )}
                </span>
                <span>▾</span>
              </button>
            </div>

            {/* Who's in office */}
            <div className="card">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                🏢 Chi c'è oggi
              </h3>
              {presenceList.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nessuno ha ancora aggiornato lo stato oggi
                </p>
              ) : (
                <div className="space-y-3">
                  {presenceList.slice(0, 10).map((p, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      {p.foto_url ? (
                        <img
                          src={`${API_URL}${p.foto_url}`}
                          alt={`${p.nome} ${p.cognome}`}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-sm font-semibold">
                          {p.nome?.charAt(0)}{p.cognome?.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {p.nome} {p.cognome}
                        </p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <span className={`w-2 h-2 rounded-full ${PRESENCE_STATUS[p.status]?.color || 'bg-gray-400'}`} />
                          {PRESENCE_STATUS[p.status]?.label || p.status}
                          {p.location && ` • ${p.location}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Post Detail Modal */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">{selectedPost.titolo}</h2>
              <button
                onClick={() => {
                  setSelectedPost(null);
                  setComments([]);
                }}
                className="btn btn-secondary"
                data-testid="close-post-detail"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {/* Post Content */}
              <div className="mb-6">
                <p className="text-muted-foreground mb-4">{selectedPost.contenuto}</p>
                <div className="text-sm text-muted-foreground">
                  <p>Pubblicato da {selectedPost.author_nome}</p>
                  <p>{format(new Date(selectedPost.created_at), 'dd MMM yyyy HH:mm', { locale: it })}</p>
                </div>
              </div>

              {/* Attachments Section */}
              {selectedPost.allegati && selectedPost.allegati.length > 0 && (
                <div className="mb-6 border-t border-border pt-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Paperclip size={16} className="text-primary" />
                    Allegati ({selectedPost.allegati.length})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {selectedPost.allegati.map((att, index) => (
                      <a
                        key={index}
                        href={`${API_URL}/api/board/attachments/post/${selectedPost._id}/${encodeURIComponent(att.nome_file)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors group"
                        data-testid="post-attachment"
                      >
                        <FileText size={20} className="text-primary flex-shrink-0" />
                        <span className="text-sm truncate flex-1">{att.nome_file}</span>
                        <Download size={16} className="text-muted-foreground group-hover:text-primary" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Comments Section */}
              <div className="border-t border-border pt-6">
                <h3 className="font-semibold mb-4">
                  Commenti ({comments.length})
                </h3>

                {/* Comments List */}
                <div className="space-y-4 mb-4">
                  {comments.map((comment) => (
                    <div
                      key={comment._id}
                      className="border border-border rounded-lg p-3"
                      data-testid="comment"
                    >
                      <p className="text-sm mb-2">{comment.contenuto}</p>
                      <div className="text-xs text-muted-foreground">
                        {comment.author_nome} •{' '}
                        {format(new Date(comment.created_at), 'dd MMM yyyy HH:mm', { locale: it })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Comment */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="form-input flex-1"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Scrivi un commento..."
                    data-testid="comment-input"
                  />
                  <button
                    className="btn btn-primary"
                    onClick={addComment}
                    data-testid="send-comment"
                  >
                    <Send size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Post Modal */}
      {showPostModal && <CreatePostModal onClose={() => { setShowPostModal(false); fetchData(); }} />}

      {/* Create Event Modal */}
      {showEventModal && <CreateEventModal 
        onClose={() => { 
          setShowEventModal(false); 
          setEditingEvent(null);
          fetchData(); 
        }} 
        editEvent={editingEvent}
      />}

      {/* Social Post Modal */}
      {showSocialPostModal && (
        <CreateSocialPostModal 
          onClose={() => setShowSocialPostModal(false)} 
          onSuccess={() => fetchData()}
          API_URL={API_URL}
        />
      )}

      {/* Presence Modal */}
      {showPresenceModal && (
        <PresenceModal
          onClose={() => setShowPresenceModal(false)}
          onUpdate={updateMyPresence}
        />
      )}

      {/* Social Post Detail Modal */}
      {selectedSocialPost && (
        <SocialPostDetailModal
          post={selectedSocialPost}
          comments={socialComments}
          currentUser={currentUser}
          onClose={() => { setSelectedSocialPost(null); setSocialComments([]); }}
          onAddComment={addSocialComment}
          onReact={reactToPost}
          API_URL={API_URL}
        />
      )}
    </div>
  );
}

// Create Post Modal Component
function CreatePostModal({ onClose }) {
  const [formData, setFormData] = useState({
    titolo: '',
    contenuto: '',
    categoria: 'annuncio',
    pinned: false,
    target_roles: ['all']
  });
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles([...files, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const token = localStorage.getItem('token');

    try {
      // First create the post
      const res = await fetch(`${API_URL}/api/board/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) throw new Error('Errore pubblicazione');
      
      const createdPost = await res.json();

      // Upload attachments if any
      if (files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const formDataFile = new FormData();
          formDataFile.append('file', file);

          await fetch(`${API_URL}/api/board/posts/${createdPost._id}/attachments`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formDataFile
          });

          setUploadProgress(Math.round(((i + 1) / files.length) * 100));
        }
      }

      alert('Annuncio pubblicato con successo');
      onClose();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Nuovo Annuncio</h2>
          <button onClick={onClose} className="btn btn-secondary p-2">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Titolo</label>
              <input
                type="text"
                className="form-input"
                value={formData.titolo}
                onChange={(e) => setFormData({ ...formData, titolo: e.target.value })}
                required
                placeholder="Oggetto dell'annuncio"
                data-testid="post-title"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Contenuto</label>
              <textarea
                className="form-input form-textarea"
                value={formData.contenuto}
                onChange={(e) => setFormData({ ...formData, contenuto: e.target.value })}
                required
                rows={6}
                placeholder="Scrivi il messaggio..."
                data-testid="post-content"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Categoria</label>
                <select
                  className="form-input"
                  value={formData.categoria}
                  onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                  data-testid="post-category"
                >
                  <option value="annuncio">Annuncio</option>
                  <option value="comunicazione">Comunicazione</option>
                  <option value="alert">Alert</option>
                </select>
              </div>

              <div className="form-group">
                <label className="flex items-center gap-2 mt-8">
                  <input
                    type="checkbox"
                    checked={formData.pinned}
                    onChange={(e) => setFormData({ ...formData, pinned: e.target.checked })}
                    data-testid="post-pinned"
                  />
                  <Pin size={16} />
                  <span>Fissa in alto</span>
                </label>
              </div>
            </div>

            {/* Allegati Section */}
            <div className="form-group">
              <label className="form-label flex items-center gap-2">
                <Paperclip size={16} className="text-primary" />
                Allegati
              </label>
              
              <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                <input
                  type="file"
                  id="post-files"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="post-files-input"
                />
                <label 
                  htmlFor="post-files" 
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Paperclip size={24} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Clicca per aggiungere allegati
                  </span>
                  <span className="text-xs text-muted-foreground">
                    PDF, DOC, XLS, immagini...
                  </span>
                </label>
              </div>

              {/* Files List */}
              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((file, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-primary" />
                        <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-destructive hover:bg-destructive/10 p-1 rounded"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Progress */}
              {saving && files.length > 0 && uploadProgress > 0 && (
                <div className="mt-2">
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Caricamento allegati: {uploadProgress}%
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 justify-end mt-6 pt-6 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Annulla
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              data-testid="submit-post"
            >
              {saving ? 'Pubblicazione...' : 'Pubblica'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Create Event Modal Component
function CreateEventModal({ onClose, editEvent = null }) {
  const isEditing = !!editEvent;
  
  const [formData, setFormData] = useState({
    titolo: editEvent?.titolo || '',
    descrizione: editEvent?.descrizione || '',
    data_inizio: editEvent?.data_inizio ? new Date(editEvent.data_inizio).toISOString().slice(0, 16) : '',
    data_fine: editEvent?.data_fine ? new Date(editEvent.data_fine).toISOString().slice(0, 16) : '',
    location: editEvent?.location || '',
    tipo: editEvent?.tipo || 'meeting'
  });
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles([...files, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const token = localStorage.getItem('token');

    try {
      const payload = {
        ...formData,
        data_inizio: new Date(formData.data_inizio).toISOString(),
        data_fine: formData.data_fine ? new Date(formData.data_fine).toISOString() : null
      };

      const url = isEditing 
        ? `${API_URL}/api/board/events/${editEvent._id}`
        : `${API_URL}/api/board/events`;
      
      const method = isEditing ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error(isEditing ? 'Errore modifica evento' : 'Errore creazione evento');

      const savedEvent = await res.json();

      // Upload attachments if any (only for new events or if new files added)
      if (files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const formDataFile = new FormData();
          formDataFile.append('file', file);

          await fetch(`${API_URL}/api/board/events/${savedEvent._id}/attachments`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formDataFile
          });

          setUploadProgress(Math.round(((i + 1) / files.length) * 100));
        }
      }

      alert(isEditing ? 'Evento modificato con successo' : 'Evento aggiunto al calendario');
      onClose();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">{isEditing ? 'Modifica Evento' : 'Nuovo Evento'}</h2>
          <button onClick={onClose} className="btn btn-secondary p-2">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Titolo Evento</label>
              <input
                type="text"
                className="form-input"
                value={formData.titolo}
                onChange={(e) => setFormData({ ...formData, titolo: e.target.value })}
                required
                placeholder="Es: Formazione Antincendio"
                data-testid="event-title"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Descrizione</label>
              <textarea
                className="form-input form-textarea"
                value={formData.descrizione}
                onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                rows={3}
                placeholder="Dettagli evento..."
                data-testid="event-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Data/Ora Inizio</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  value={formData.data_inizio}
                  onChange={(e) => setFormData({ ...formData, data_inizio: e.target.value })}
                  required
                  data-testid="event-start"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Data/Ora Fine (opzionale)</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  value={formData.data_fine}
                  onChange={(e) => setFormData({ ...formData, data_fine: e.target.value })}
                  data-testid="event-end"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Tipo</label>
                <select
                  className="form-input"
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  data-testid="event-type"
                >
                  <option value="meeting">Meeting</option>
                  <option value="formazione">Formazione</option>
                  <option value="scadenza">Scadenza</option>
                  <option value="evento">Evento</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Location</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Sede / Online"
                  data-testid="event-location"
                />
              </div>
            </div>

            {/* Allegati Section */}
            <div className="form-group">
              <label className="form-label flex items-center gap-2">
                <Paperclip size={16} className="text-primary" />
                Allegati (programma, materiale corso, ecc.)
              </label>
              
              <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                <input
                  type="file"
                  id="event-files"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="event-files-input"
                />
                <label 
                  htmlFor="event-files" 
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Paperclip size={24} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Clicca per aggiungere allegati
                  </span>
                  <span className="text-xs text-muted-foreground">
                    PDF, DOC, XLS, immagini...
                  </span>
                </label>
              </div>

              {/* Files List */}
              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((file, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-primary" />
                        <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-destructive hover:bg-destructive/10 p-1 rounded"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Progress */}
              {saving && files.length > 0 && uploadProgress > 0 && (
                <div className="mt-2">
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Caricamento allegati: {uploadProgress}%
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 justify-end mt-6 pt-6 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Annulla
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              data-testid="submit-event"
            >
              {saving ? 'Salvataggio...' : (isEditing ? 'Salva Modifiche' : 'Aggiungi al Calendario')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Component to render post content with @mentions highlighted
function PostContentWithTags({ content, tags, taggedUsers, API_URL }) {
  // Parse content and highlight @mentions
  const renderContent = () => {
    if (!content) return null;
    
    // Regex to find @mentions (format: @Nome_Cognome)
    const mentionRegex = /@(\w+_\w+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = mentionRegex.exec(content)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }
      
      // Add mention as styled link
      const mentionName = match[1].replace('_', ' ');
      parts.push(
        <span 
          key={match.index} 
          className="text-primary font-medium hover:underline cursor-pointer"
          title={mentionName}
        >
          @{mentionName}
        </span>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }
    
    return parts.length > 0 ? parts : content;
  };
  
  return (
    <p className="whitespace-pre-wrap">
      {renderContent()}
    </p>
  );
}

// Social Post Card Component
function SocialPostCard({ post, currentUser, onReact, onComment, onDelete, onVote, API_URL }) {
  const [showReactions, setShowReactions] = useState(false);
  
  const totalReactions = Object.values(post.reactions || {}).reduce((a, b) => a + b, 0);
  const isAuthor = post.author_id === currentUser?.user_id;
  const isAdmin = currentUser?.role === 'admin';
  
  return (
    <div className="card" data-testid="social-post">
      {/* Post Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {post.author_foto ? (
            <img
              src={`${API_URL}${post.author_foto}`}
              alt={post.author_nome}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold">
              {post.author_nome?.charAt(0) || '?'}
            </div>
          )}
          <div>
            <p className="font-semibold">{post.author_nome}</p>
            <p className="text-xs text-muted-foreground">
              {post.author_ruolo && <span>{post.author_ruolo} • </span>}
              {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: it })}
            </p>
          </div>
        </div>
        {(isAuthor || isAdmin) && (
          <button
            className="text-muted-foreground hover:text-destructive p-2"
            onClick={() => onDelete(post._id)}
            title="Elimina post"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {/* Post Content */}
      <div className="mb-4">
        <PostContentWithTags content={post.contenuto} tags={post.tags} taggedUsers={post.tagged_users} API_URL={API_URL} />
        
        {/* Hashtags */}
        {post.hashtags && post.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {post.hashtags.map((tag, idx) => (
              <span key={idx} className="text-primary text-sm">#{tag}</span>
            ))}
          </div>
        )}
      </div>

      {/* Media */}
      {post.media_urls && post.media_urls.length > 0 && (
        <div className={`mb-4 grid gap-2 ${post.media_urls.length > 1 ? 'grid-cols-2' : ''}`}>
          {post.media_urls.map((url, idx) => (
            <a 
              key={idx} 
              href={`${API_URL}${url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-lg overflow-hidden bg-muted"
            >
              <img
                src={`${API_URL}${url}`}
                alt=""
                className="w-full h-auto max-h-96 object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </a>
          ))}
        </div>
      )}

      {/* Poll */}
      {post.tipo === 'sondaggio' && post.poll && (
        <div className="mb-4 space-y-2">
          <p className="font-semibold mb-3">{post.poll.domanda}</p>
          {post.poll.opzioni.map((opt, idx) => {
            const totalVotes = post.poll.opzioni.reduce((a, b) => a + b.voti, 0);
            const percentage = totalVotes > 0 ? Math.round((opt.voti / totalVotes) * 100) : 0;
            const hasVoted = opt.voters?.includes(currentUser?.user_id);
            
            return (
              <button
                key={idx}
                className={`w-full text-left p-3 rounded-lg border transition-all relative overflow-hidden ${
                  hasVoted ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
                onClick={() => onVote(post._id, idx)}
              >
                <div 
                  className="absolute inset-0 bg-primary/10 transition-all"
                  style={{ width: `${percentage}%` }}
                />
                <div className="relative flex justify-between">
                  <span>{opt.testo}</span>
                  <span className="font-semibold">{percentage}%</span>
                </div>
              </button>
            );
          })}
          <p className="text-xs text-muted-foreground mt-2">
            {post.poll.opzioni.reduce((a, b) => a + b.voti, 0)} voti
          </p>
        </div>
      )}

      {/* Reactions Summary */}
      {totalReactions > 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground pb-3 border-b border-border">
          <div className="flex -space-x-1">
            {Object.entries(post.reactions).slice(0, 3).map(([tipo, count]) => (
              count > 0 && <span key={tipo}>{REACTIONS[tipo]?.emoji}</span>
            ))}
          </div>
          <span>{totalReactions}</span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-1 pt-3">
        <div className="relative flex-1">
          <button
            className={`w-full btn btn-secondary btn-sm justify-center gap-2 ${
              post.user_reaction ? 'text-primary' : ''
            }`}
            onClick={() => setShowReactions(!showReactions)}
            onMouseEnter={() => setShowReactions(true)}
          >
            {post.user_reaction ? REACTIONS[post.user_reaction]?.emoji : <ThumbsUp size={16} />}
            <span>{post.user_reaction ? REACTIONS[post.user_reaction]?.label : 'Mi piace'}</span>
          </button>
          
          {/* Reaction Picker */}
          {showReactions && (
            <div 
              className="absolute bottom-full left-0 mb-2 bg-card rounded-full shadow-lg border border-border p-2 flex gap-1"
              onMouseLeave={() => setShowReactions(false)}
            >
              {Object.entries(REACTIONS).map(([tipo, { emoji, label }]) => (
                <button
                  key={tipo}
                  className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center text-xl transition-transform hover:scale-125"
                  onClick={() => {
                    onReact(post._id, tipo);
                    setShowReactions(false);
                  }}
                  title={label}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
        
        <button
          className="flex-1 btn btn-secondary btn-sm justify-center gap-2"
          onClick={onComment}
        >
          <MessageSquare size={16} />
          Commenta
          {post.comments_count > 0 && <span>({post.comments_count})</span>}
        </button>
      </div>
    </div>
  );
}

// Create Social Post Modal with @mention support
function CreateSocialPostModal({ onClose, onSuccess, API_URL }) {
  const [contenuto, setContenuto] = useState('');
  const [tipo, setTipo] = useState('post');
  const [files, setFiles] = useState([]);
  const [hashtags, setHashtags] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Tag mention states
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionResults, setMentionResults] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  
  // Poll fields
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Search users for @mention
  const searchUsers = async (query) => {
    if (query.length < 2) {
      setMentionResults([]);
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/social/search/users?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const users = await res.json();
        setMentionResults(users);
      }
    } catch (err) {
      console.error('Error searching users:', err);
    }
  };

  // Handle text change with @mention detection
  const handleTextChange = (e) => {
    const text = e.target.value;
    const cursor = e.target.selectionStart;
    setContenuto(text);
    setCursorPosition(cursor);
    
    // Check if user is typing a mention
    const textBeforeCursor = text.slice(0, cursor);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (mentionMatch) {
      setMentionSearch(mentionMatch[1]);
      setShowMentions(true);
      searchUsers(mentionMatch[1]);
    } else {
      setShowMentions(false);
      setMentionSearch('');
    }
  };

  // Insert @mention
  const insertMention = (user) => {
    const textBeforeMention = contenuto.slice(0, cursorPosition).replace(/@\w*$/, '');
    const textAfterMention = contenuto.slice(cursorPosition);
    const mentionText = `@${user.nome}_${user.cognome}`;
    
    const newText = textBeforeMention + mentionText + ' ' + textAfterMention;
    setContenuto(newText);
    
    // Add to selected tags if not already added
    if (!selectedTags.find(t => t.id === user.id)) {
      setSelectedTags([...selectedTags, user]);
    }
    
    setShowMentions(false);
    setMentionSearch('');
    
    // Focus back on textarea
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newCursorPos = textBeforeMention.length + mentionText.length + 1;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // Remove tag
  const removeTag = (userId) => {
    setSelectedTags(selectedTags.filter(t => t.id !== userId));
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles([...files, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const addPollOption = () => {
    if (pollOptions.length < 6) {
      setPollOptions([...pollOptions, '']);
    }
  };

  const updatePollOption = (index, value) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (tipo === 'sondaggio') {
      if (!pollQuestion.trim() || pollOptions.filter(o => o.trim()).length < 2) {
        alert('Inserisci una domanda e almeno 2 opzioni');
        return;
      }
    } else if (!contenuto.trim() && files.length === 0) {
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      if (tipo === 'sondaggio') {
        // Create poll
        const formData = new FormData();
        formData.append('domanda', pollQuestion);
        formData.append('opzioni', pollOptions.filter(o => o.trim()).join(','));
        
        const res = await fetch(`${API_URL}/api/social/polls`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (!res.ok) throw new Error('Errore creazione sondaggio');
      } else {
        // Create regular post with tags
        const formData = new FormData();
        formData.append('contenuto', contenuto);
        formData.append('tipo', files.length > 0 ? 'foto' : 'post');
        formData.append('hashtags', hashtags);
        formData.append('tags', selectedTags.map(t => t.id).join(','));
        
        files.forEach(file => {
          formData.append('files', file);
        });

        const res = await fetch(`${API_URL}/api/social/posts`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (!res.ok) throw new Error('Errore pubblicazione');
      }

      onSuccess();
      onClose();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Crea Post</h2>
          <button onClick={onClose} className="btn btn-secondary p-2">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Post Type Selector */}
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              className={`flex-1 btn ${tipo === 'post' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTipo('post')}
            >
              <MessageSquare size={16} />
              Post
            </button>
            <button
              type="button"
              className={`flex-1 btn ${tipo === 'sondaggio' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTipo('sondaggio')}
            >
              <BarChart3 size={16} />
              Sondaggio
            </button>
          </div>

          {tipo === 'sondaggio' ? (
            /* Poll Form */
            <div className="space-y-4">
              <div className="form-group">
                <label className="form-label">Domanda</label>
                <input
                  type="text"
                  className="form-input"
                  value={pollQuestion}
                  onChange={(e) => setPollQuestion(e.target.value)}
                  placeholder="Fai una domanda..."
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Opzioni</label>
                <div className="space-y-2">
                  {pollOptions.map((opt, idx) => (
                    <input
                      key={idx}
                      type="text"
                      className="form-input"
                      value={opt}
                      onChange={(e) => updatePollOption(idx, e.target.value)}
                      placeholder={`Opzione ${idx + 1}`}
                    />
                  ))}
                </div>
                {pollOptions.length < 6 && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm mt-2"
                    onClick={addPollOption}
                  >
                    <Plus size={14} />
                    Aggiungi opzione
                  </button>
                )}
              </div>
            </div>
          ) : (
            /* Regular Post Form */
            <div className="space-y-4">
              <div className="form-group relative">
                <textarea
                  ref={textareaRef}
                  className="form-input form-textarea"
                  value={contenuto}
                  onChange={handleTextChange}
                  rows={4}
                  placeholder="Cosa stai pensando? Usa @ per taggare i colleghi"
                  data-testid="social-post-content"
                />
                
                {/* Mention Autocomplete Dropdown */}
                {showMentions && mentionResults.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto z-10">
                    {mentionResults.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        className="w-full flex items-center gap-3 p-3 hover:bg-muted transition-colors text-left"
                        onClick={() => insertMention(user)}
                      >
                        {user.foto_url ? (
                          <img
                            src={`${API_URL}${user.foto_url}`}
                            alt=""
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-semibold">
                            {user.nome?.charAt(0)}{user.cognome?.charAt(0)}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-sm">{user.nome} {user.cognome}</p>
                          {user.ruolo && <p className="text-xs text-muted-foreground">{user.ruolo}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Tags Preview */}
              {selectedTags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedTags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-full text-sm"
                    >
                      @{tag.nome} {tag.cognome}
                      <button
                        type="button"
                        onClick={() => removeTag(tag.id)}
                        className="hover:bg-primary/20 rounded-full p-0.5"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Hashtags */}
              <div className="form-group">
                <label className="form-label text-sm">Hashtags (separati da virgola)</label>
                <input
                  type="text"
                  className="form-input"
                  value={hashtags}
                  onChange={(e) => setHashtags(e.target.value)}
                  placeholder="es: teambuilding, ufficio, successo"
                />
              </div>

              {/* File Upload */}
              <div className="form-group">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  multiple
                  accept="image/*,video/*"
                  className="hidden"
                />
                <button
                  type="button"
                  className="btn btn-secondary w-full"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Image size={18} className="text-green-500" />
                  Aggiungi foto/video
                </button>
              </div>

              {/* File Previews */}
              {files.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {files.map((file, idx) => (
                    <div key={idx} className="relative aspect-square bg-muted rounded-lg overflow-hidden">
                      {file.type.startsWith('image/') ? (
                        <img
                          src={URL.createObjectURL(file)}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FileText size={24} className="text-muted-foreground" />
                        </div>
                      )}
                      <button
                        type="button"
                        className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center text-white"
                        onClick={() => removeFile(idx)}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 justify-end mt-6 pt-6 border-t border-border">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Annulla
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Pubblicazione...' : 'Pubblica'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Presence Modal
function PresenceModal({ onClose, onUpdate }) {
  const [status, setStatus] = useState('in_ufficio');
  const [location, setLocation] = useState('');
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg max-w-md w-full">
        <div className="border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Aggiorna il tuo stato</h2>
          <button onClick={onClose} className="btn btn-secondary p-2">
            <X size={18} />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(PRESENCE_STATUS).map(([key, { label, emoji }]) => (
              <button
                key={key}
                className={`p-3 rounded-lg border text-left transition-all ${
                  status === key 
                    ? 'border-primary bg-primary/10' 
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setStatus(key)}
              >
                <span className="text-xl mr-2">{emoji}</span>
                <span className="text-sm">{label}</span>
              </button>
            ))}
          </div>
          
          {(status === 'fuori_sede' || status === 'smart_working') && (
            <div className="form-group">
              <label className="form-label">Dove ti trovi?</label>
              <input
                type="text"
                className="form-input"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="es: Cliente ABC, Casa"
              />
            </div>
          )}
        </div>
        
        <div className="border-t border-border px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-secondary">Annulla</button>
          <button 
            className="btn btn-primary"
            onClick={() => onUpdate(status, location)}
          >
            Aggiorna
          </button>
        </div>
      </div>
    </div>
  );
}

// Social Post Detail Modal
function SocialPostDetailModal({ post, comments, currentUser, onClose, onAddComment, onReact, API_URL }) {
  const [newComment, setNewComment] = useState('');
  const [showReactions, setShowReactions] = useState(false);
  
  const handleSubmitComment = (e) => {
    e.preventDefault();
    if (newComment.trim()) {
      onAddComment(newComment);
      setNewComment('');
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Post di {post.author_nome}</h2>
          <button onClick={onClose} className="btn btn-secondary p-2">
            <X size={18} />
          </button>
        </div>
        
        <div className="p-6">
          {/* Post Header */}
          <div className="flex items-center gap-3 mb-4">
            {post.author_foto ? (
              <img
                src={`${API_URL}${post.author_foto}`}
                alt={post.author_nome}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold">
                {post.author_nome?.charAt(0)}
              </div>
            )}
            <div>
              <p className="font-semibold">{post.author_nome}</p>
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: it })}
              </p>
            </div>
          </div>
          
          {/* Content */}
          <p className="mb-4 whitespace-pre-wrap">{post.contenuto}</p>
          
          {/* Media */}
          {post.media_urls && post.media_urls.length > 0 && (
            <div className="mb-4">
              {post.media_urls.map((url, idx) => (
                <img
                  key={idx}
                  src={`${API_URL}${url}`}
                  alt=""
                  className="w-full rounded-lg"
                />
              ))}
            </div>
          )}
          
          {/* Reactions */}
          <div className="flex items-center gap-4 py-3 border-y border-border mb-4">
            <div className="relative">
              <button
                className={`btn btn-secondary btn-sm ${post.user_reaction ? 'text-primary' : ''}`}
                onClick={() => setShowReactions(!showReactions)}
              >
                {post.user_reaction ? REACTIONS[post.user_reaction]?.emoji : <ThumbsUp size={16} />}
                <span className="ml-1">
                  {post.user_reaction ? REACTIONS[post.user_reaction]?.label : 'Mi piace'}
                </span>
              </button>
              
              {showReactions && (
                <div className="absolute bottom-full left-0 mb-2 bg-card rounded-full shadow-lg border border-border p-2 flex gap-1">
                  {Object.entries(REACTIONS).map(([tipo, { emoji, label }]) => (
                    <button
                      key={tipo}
                      className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center text-xl transition-transform hover:scale-125"
                      onClick={() => {
                        onReact(post._id, tipo);
                        setShowReactions(false);
                      }}
                      title={label}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Comments */}
          <h3 className="font-semibold mb-3">Commenti ({comments.length})</h3>
          
          <div className="space-y-3 mb-4">
            {comments.map((comment) => (
              <div key={comment._id} className="flex gap-3">
                {comment.author_foto ? (
                  <img
                    src={`${API_URL}${comment.author_foto}`}
                    alt=""
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-semibold">
                    {comment.author_nome?.charAt(0)}
                  </div>
                )}
                <div className="flex-1 bg-muted/50 rounded-lg px-3 py-2">
                  <p className="font-semibold text-sm">{comment.author_nome}</p>
                  <p className="text-sm">{comment.contenuto}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: it })}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Add Comment */}
          <form onSubmit={handleSubmitComment} className="flex gap-2">
            <input
              type="text"
              className="form-input flex-1"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Scrivi un commento..."
            />
            <button type="submit" className="btn btn-primary">
              <Send size={18} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Board;
